import { Flexbox, Text } from '@morressier/ts';
import { useForm, Controller } from 'react-hook-form';
import styled from 'styled-components';
import {
  faChevronRight,
  faChevronLeft
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { Dropdown, ActionModalContent } from 'components/ProfileUIComponents';
import { VIEWS } from '../EmailAffiliation';
import {
  Container,
  MediumInput,
  ModalForm,
  SmallInput,
  SmallText
} from '../style';
import { countriesOptions } from '../../../countriesList';
import { OrganizationFormValues } from 'typings/profile';
import { urlRegex } from 'utils/constants';
import { logEvent } from 'utils/eventTracking';

const StyledDropdown = styled(Dropdown)`
  width: 35rem;
  margin: 8px 0;
`;

interface IDetailProps {
  handleNavigation: (nextView: VIEWS) => void;
  onDismiss: () => void;
  isWithoutEmailAccess: boolean;
  onSubmit: (data: OrganizationFormValues) => void;
  defaultValue: OrganizationFormValues;
}

const UserProvidedAffiliationView = (props: IDetailProps) => {
  const {
    handleNavigation,
    onDismiss,
    onSubmit,
    isWithoutEmailAccess,
    defaultValue
  } = props;
  const {
    handleSubmit,
    control,
    formState: { errors }
  } = useForm<OrganizationFormValues>({ defaultValues: defaultValue });

  const onSecondaryClick = () => {
    if (isWithoutEmailAccess) {
      logEvent(
        'PROFILE_ADD_AFFILIATION_ORGANIZATION_CREATE_USE_DIFFERENT_ORGANIZATION_CLICKED'
      );
      handleNavigation(VIEWS.SearchOrganizationView);
    } else {
      logEvent(
        'PROFILE_ADD_AFFILIATION_ORGANIZATION_CREATE_USE_DIFFERENT_EMAIL_CLICKED'
      );
      handleNavigation(VIEWS.EmailView);
    }
  };

  const addUserAffiliation = async (data: OrganizationFormValues) => {
    logEvent('PROFILE_ADD_AFFILIATION_ORGANIZATION_CREATE_CONTINUE', {
      organization_name: data.organizationName,
      organization_country: data.organizationCountryAlpha2,
      organization_postal_code: data.organizationPostalCode
    });
    onSubmit(data);
    handleNavigation(VIEWS.DetailsView);
  };

  return (
    <ModalForm onSubmit={handleSubmit(addUserAffiliation)}>
      <ActionModalContent
        title="Add affiliation"
        primaryAction={{
          title: 'Continue',
          iconAfter: <FontAwesomeIcon icon={faChevronRight} />,
          type: 'submit'
        }}
        secondaryAction={{
          title: isWithoutEmailAccess ? 'Update organization' : 'Update email',
          iconBefore: <FontAwesomeIcon icon={faChevronLeft} />,
          onClick: onSecondaryClick,
          type: 'button'
        }}
        cautionaryAction={{
          title: 'Exit without saving',
          onClick: () => {
            logEvent('PROFILE_AFFILIATION_EXIT_WITHOUT_SAVING_CLICKED', {
              step: 'SEARCH_BY_NAME'
            });
            onDismiss();
          },
          type: 'button'
        }}
      >
        <Container>
          <Text size="h3_new" fontWeight="bold">
            Add your organization
          </Text>
          <Text>
            If your organization is not already present in our list of
            established affiliations, you can add it manually.
          </Text>
        </Container>
        <Container>
          <Text fontWeight="bold">Organization</Text>
          <Text>Use the official U.S. English name where possible.</Text>
          <Controller
            name="organizationName"
            control={control}
            rules={{
              required: 'Organization is required',
              maxLength: {
                value: 255,
                message:
                  'Organization Name value exceeds maximum length of 255 characters.'
              }
            }}
            render={({ field }) => (
              <MediumInput
                placeholder="e.g. Morressier"
                {...field}
                error={errors.organizationName?.message}
              />
            )}
          />
        </Container>
        <Container>
          <Text fontWeight="bold">Street Address</Text>
          <Controller
            name="organizationAddress"
            control={control}
            render={({ field }) => (
              <MediumInput
                placeholder="e.g. Zossener Str. 55"
                {...field}
                error={errors.organizationAddress?.message}
              />
            )}
            rules={{
              maxLength: {
                value: 255,
                message:
                  'Street Address value exceeds maximum length of 255 characters.'
              }
            }}
          />
        </Container>
        <Container>
          <Text fontWeight="bold">City</Text>
          <Controller
            name="organizationCity"
            control={control}
            rules={{
              required: 'City is required',
              maxLength: {
                value: 255,
                message: 'City value exceeds maximum length of 255 characters.'
              }
            }}
            render={({ field }) => (
              <MediumInput
                placeholder="e.g. Berlin"
                {...field}
                error={errors.organizationCity?.message}
              />
            )}
          />
        </Container>
        <Flexbox flexDirection="row" gap={16}>
          <Container>
            <Text fontWeight="bold">State/Province/Region</Text>
            <Controller
              name="organizationState"
              control={control}
              render={({ field }) => (
                <SmallInput
                  placeholder="e.g. Brandenburg"
                  {...field}
                  error={errors.organizationState?.message}
                />
              )}
              rules={{
                maxLength: {
                  value: 255,
                  message:
                    'State value exceeds maximum length of 255 characters.'
                }
              }}
            />
          </Container>
          <Container>
            <Text fontWeight="bold">Zip/Postal code</Text>
            <Controller
              name="organizationPostalCode"
              control={control}
              render={({ field }) => (
                <SmallInput
                  placeholder="e.g. 10247"
                  {...field}
                  error={errors.organizationPostalCode?.message}
                />
              )}
              rules={{
                maxLength: {
                  value: 255,
                  message:
                    'Zip code value exceeds maximum length of 255 characters.'
                }
              }}
            />
          </Container>
        </Flexbox>
        <Container>
          <Text fontWeight="bold">Country</Text>
          <Controller
            name="organizationCountryAlpha2"
            control={control}
            rules={{ required: 'Country is required' }}
            render={({ field }) => (
              <StyledDropdown
                options={countriesOptions}
                isSearchable
                placeholder="Select Country"
                error={errors.organizationCountryAlpha2?.message}
                {...field}
              />
            )}
          />
        </Container>
        <Container>
          <Text fontWeight="bold">URL</Text>
          <SmallText>
            Provide direct link to main organization page if possible, e.g.
            www.morressier.com, (not www.morressier.com/events/39387292).
          </SmallText>
          <Controller
            name="organizationUrl"
            control={control}
            render={({ field }) => (
              <MediumInput
                placeholder="e.g. www.morressier.com"
                {...field}
                error={errors.organizationUrl?.message}
              />
            )}
            rules={{
              maxLength: {
                value: 255,
                message: 'URL value exceeds maximum length of 255 characters.'
              },
              pattern: {
                value: urlRegex,
                message: 'Invalid URL'
              }
            }}
          />
        </Container>
      </ActionModalContent>
    </ModalForm>
  );
};
export default UserProvidedAffiliationView;
